import 'flatpickr/dist/flatpickr.css';
import '~/styles/itinerary-search-controls.css';

import { useEffect, useId, useLayoutEffect, useRef } from 'react';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { Box, FormHelperText, InputAdornment, TextField, useMediaQuery, useTheme } from '@mui/material';
import flatpickr from 'flatpickr';
import rangePlugin from 'flatpickr/dist/plugins/rangePlugin';
import type { Instance } from 'flatpickr/dist/types/instance';

type Props = {
  departureDate: Date | null;
  returnDate: Date | null;
  departureLabel: string;
  returnLabel: string;
  departureMinDate: Date;
  departureMaxDate?: Date;
  returnMaxDate?: Date;
  returnMinDaysAfterDeparture: number;
  singleDateMode?: boolean;
  departureErrors?: string[];
  returnErrors?: string[];
  onDatesChange: (departureDate: Date | null, returnDate: Date | null) => void;
};

const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};
const normalizeDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value as string | number);

  return Number.isNaN(date.getTime()) ? null : date;
};

function ItineraryDateRangeSelector({
  departureDate,
  departureErrors,
  departureLabel,
  departureMaxDate,
  departureMinDate,
  onDatesChange,
  returnDate,
  returnErrors,
  returnLabel,
  returnMaxDate,
  returnMinDaysAfterDeparture,
  singleDateMode,
}: Props) {
  const departureInputRef = useRef<HTMLInputElement>(null);
  const returnInputRef = useRef<HTMLInputElement>(null);
  const instanceRef = useRef<Instance | null>(null);
  const inputId = `itinerary-return-${useId().replace(/:/g, '')}`;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const onDatesChangeRef = useRef(onDatesChange);
  const normalizedDepartureDate = normalizeDate(departureDate);
  const normalizedReturnDate = normalizeDate(returnDate);

  useLayoutEffect(() => {
    onDatesChangeRef.current = onDatesChange;
  }, [onDatesChange]);

  useEffect(() => {
    const departureInput = departureInputRef.current;
    const returnInput = returnInputRef.current;
    if (!departureInput || (!singleDateMode && !returnInput)) return undefined;

    const instance = flatpickr(departureInput, {
      altFormat: 'D, d M Y',
      altInput: true,
      allowInput: false,
      clickOpens: true,
      dateFormat: 'Y-m-d',
      defaultDate: singleDateMode ? (normalizedDepartureDate ?? undefined) : [normalizedDepartureDate, normalizedReturnDate].filter(Boolean) as Date[],
      disableMobile: true,
      maxDate: departureMaxDate,
      minDate: departureMinDate,
      mode: singleDateMode ? 'single' : 'range',
      onChange: (selectedDates, _, calendar) => {
        const nextDeparture = selectedDates[0] ?? null;
        const minimumReturn = nextDeparture ? addDays(nextDeparture, returnMinDaysAfterDeparture) : null;
        const selectedReturn = selectedDates[1] ?? null;
        const nextReturn = selectedReturn && minimumReturn && selectedReturn < minimumReturn ? null : selectedReturn;

        if (selectedReturn && !nextReturn && nextDeparture) calendar.setDate([nextDeparture], false);
        onDatesChangeRef.current(nextDeparture, singleDateMode ? null : nextReturn);
      },
      onOpen: (_, __, calendar) => {
        if (!isMobile || calendar.calendarContainer.querySelector('[data-itinerary-calendar-done]')) return;
        const header = document.createElement('div');
        header.className = 'itinerary-calendar-mobile-header';
        header.innerHTML = '<strong>Select dates</strong><button type="button" data-itinerary-calendar-done>Done</button>';
        header.querySelector('button')?.addEventListener('click', () => calendar.close());
        calendar.calendarContainer.prepend(header);
      },
      plugins: singleDateMode || !returnInput ? [] : [rangePlugin({ input: `#${inputId}` })],
      showMonths: !singleDateMode && !isMobile ? 2 : 1,
    }) as Instance;

    instanceRef.current = instance;
    const altInput = instance.altInput;
    if (altInput) {
      altInput.autocomplete = 'off';
      altInput.setAttribute('aria-label', departureLabel);
      altInput.setAttribute('inputmode', 'none');
    }
    if (returnInput) {
      returnInput.autocomplete = 'off';
      returnInput.setAttribute('inputmode', 'none');
    }

    return () => {
      instance.destroy();
      instanceRef.current = null;
    };
  }, [departureLabel, departureMaxDate?.getTime(), departureMinDate.getTime(), inputId, isMobile, returnMaxDate?.getTime(), returnMinDaysAfterDeparture, singleDateMode]);

  useEffect(() => {
    const selectedDates = singleDateMode ? (normalizedDepartureDate ? [normalizedDepartureDate] : []) : [normalizedDepartureDate, normalizedReturnDate].filter(Boolean) as Date[];
    const instance = instanceRef.current;
    if (!instance) return;
    const unchanged = selectedDates.length === instance.selectedDates.length
      && selectedDates.every((date, index) => date.getTime() === instance.selectedDates[index].getTime());
    if (!unchanged) instance.setDate(selectedDates, false);
    // Each input represents one endpoint, not the formatted range.
    if (instance.altInput) instance.altInput.value = normalizedDepartureDate ? flatpickr.formatDate(normalizedDepartureDate, 'D, d M Y') : '';
    if (returnInputRef.current) returnInputRef.current.value = normalizedReturnDate ? flatpickr.formatDate(normalizedReturnDate, 'D, d M Y') : '';
  }, [normalizedDepartureDate?.getTime(), normalizedReturnDate?.getTime(), singleDateMode]);

  useEffect(() => {
    if (!instanceRef.current) return;
    instanceRef.current.set('maxDate', departureMaxDate);
    if (!singleDateMode && returnMaxDate && departureMaxDate && returnMaxDate < departureMaxDate) instanceRef.current.set('maxDate', returnMaxDate);
  }, [departureMaxDate, returnMaxDate, singleDateMode]);

  const adornment = <InputAdornment position="start"><DateRangeIcon /></InputAdornment>;

  return (
    <Box className="itinerary-date-range" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: singleDateMode ? '1fr' : '1fr 1fr' } }}>
      <Box>
        <TextField inputRef={departureInputRef} fullWidth required label={departureLabel} error={Boolean(departureErrors?.length)} inputProps={{ autoComplete: 'off', readOnly: true }} InputProps={{ startAdornment: adornment }} />
        {departureErrors?.length ? <FormHelperText error>{departureErrors.join('')}</FormHelperText> : null}
      </Box>
      {!singleDateMode ? (
        <Box>
          <TextField id={inputId} inputRef={returnInputRef} fullWidth required label={returnLabel} error={Boolean(returnErrors?.length)} inputProps={{ autoComplete: 'off', readOnly: true }} InputProps={{ startAdornment: adornment }} />
          {returnErrors?.length ? <FormHelperText error>{returnErrors.join('')}</FormHelperText> : null}
        </Box>
      ) : null}
    </Box>
  );
}

export default ItineraryDateRangeSelector;